import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import {
  CompanyPageCompanyFragment,
  useCompanyGetDefaultReportsQuery,
} from 'generated/graphql';
import { actorRoleLabels } from 'helpers/actorRoles';
import { projectTeamEmailToOptionsMap } from 'helpers/projectTeamEmailMode';
import { useState } from 'react';
import {
  displayBooleanValueAsYesNo,
  getAssessmentExpiryUnitString,
  reasonableAdjustmentKindToLabelMap,
} from 'views/Company/CompanyView/components/AssessmentsSection/AssessmentsSection.helpers';
import AssessmentsSectionForm from 'views/Company/CompanyView/components/AssessmentsSectionForm/AssessmentsSectionForm';
import { Button } from '@spotted-zebra-uk/ui-components';
import styles from './AssessmentsSection.module.scss';

type AssessmentsSectionProps = {
  company: CompanyPageCompanyFragment;
};

export function AssessmentsSection({ company }: AssessmentsSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const companyDefaultReportsQueryResponse = useCompanyGetDefaultReportsQuery();

  const defaultManagerReportId =
    companyDefaultReportsQueryResponse.data?.CompanyGetDefaultReports
      ?.managerReport?.id;
  const defaultCandidateReportId =
    companyDefaultReportsQueryResponse.data?.CompanyGetDefaultReports
      ?.candidateReport?.id;

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  return isEditing ? (
    <AssessmentsSectionForm
      company={company}
      onCancelClick={handleCancelClick}
      onSave={handleSave}
      defaultManagerReportId={defaultManagerReportId}
      defaultCandidateReportId={defaultCandidateReportId}
    />
  ) : (
    <AssessmentsSectionView company={company} onEditClick={handleEditClick} />
  );
}

type AssessmentsSectionViewProps = {
  company: CompanyPageCompanyFragment;
  onEditClick: () => void;
};

function AssessmentsSectionView({
  company,
  onEditClick,
}: AssessmentsSectionViewProps) {
  const assessmentExpiryLabel = company.assessmentExpiryOption
    ? getAssessmentExpiryUnitString(
        company.assessmentExpiryOption.duration,
        company.assessmentExpiryOption.unit,
      )
    : 'Disabled';

  const reasonableAdjustmentLabelValue = company.reasonableAdjustmentsOption
    ? reasonableAdjustmentKindToLabelMap[
        company.reasonableAdjustmentsOption?.kind
      ](company.reasonableAdjustmentsOption.value || undefined)
    : 'N/A';

  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Assessments</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        <InfoItem
          label="Allow company users to access report enablement settings and view reports via the company app"
          // If hideReports is true, we need to display the answer as No.
          value={displayBooleanValueAsYesNo(!company.hideReports)}
        />
        <div className={styles.reportSelectContainer}>
          <InfoItem
            label="Hiring team report generation"
            value={displayBooleanValueAsYesNo(company.generateManagerReport)}
          />
          <InfoItem
            label="Hiring team report"
            value={company.managerReport ? company.managerReport.name : 'N/A'}
          />
          <InfoItem
            label="Candidate report generation"
            value={displayBooleanValueAsYesNo(company.generateCandidateReport)}
          />
          <InfoItem
            label="Email candidate the report"
            value={displayBooleanValueAsYesNo(company.emailCandidateReport)}
          />
          <InfoItem
            label="Candidate report"
            value={
              company.candidateReport ? company.candidateReport.name : 'N/A'
            }
          />
        </div>
        <InfoItem
          label="Candidate reasonable adjustments experience"
          value={reasonableAdjustmentLabelValue}
        />
        <InfoItem
          label="Project-level assessment expiry notification"
          value={assessmentExpiryLabel}
        />
        <InfoItem
          label="Assessment completion emails"
          value={
            projectTeamEmailToOptionsMap[company.projectTeamEmailSettings.mode]
              ?.label || 'N/A'
          }
        />
        <InfoItem
          label="Hiring team email roles"
          value={
            company.projectTeamEmailSettings.actorRoles
              .map((role) => actorRoleLabels[role])
              .join(', ') || 'N/A'
          }
        />
        <InfoItem
          label="Cognitive tests"
          value={company.areCognitiveTestsBlocked ? 'Disabled' : 'Enabled'}
        />
        <InfoItem
          label="Multiple assessment stages per role"
          value={
            company.areMultiAssessmentStagesEnabled ? 'Enabled' : 'Disabled'
          }
        />
        <InfoItem
          label="Personality assessment mandatory"
          value={company.isPersonalityTestMandatory ? 'Enabled' : 'Disabled'}
        />
        <InfoItem
          label="AI interview stages"
          value={company.isAiInterviewEnabled ? 'Enabled' : 'Disabled'}
        />
      </div>
    </div>
  );
}
